// PATH: src/pages/Calendario.jsx
// VERSION: 2.4 — Eventos puntuales + fijos (firstSundayOnly) con color por evento

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Flex,
  IconButton,
  SimpleGrid,
  Text,
  Badge,
  VStack,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Image,
  useBreakpointValue,
  Select,
  Switch,
  FormControl,
  FormLabel,

  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { ArrowLeftIcon, ArrowRightIcon } from '@chakra-ui/icons';
import { FiClock, FiMapPin } from 'react-icons/fi';
import { API } from '../utils/apiBase';
import { cld } from '../utils/cloudinary';

const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const WEEKDAY_LABELS_LONG = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
];
const MONTHS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const EVENT_PLACEHOLDER =
  'https://via.placeholder.com/800x500?text=Imagen+no+cargada';

// Helper para unificar la obtención de la imagen del evento
function getEventImageSrc(ev, opts = {}) {
  const { width = 800, height = 500 } = opts;
  if (!ev?.image) return EVENT_PLACEHOLDER;

  // Si ya es URL absoluta, úsala tal cual
  if (/^https?:\/\//i.test(ev.image)) {
    return ev.image;
  }

  // Si es un public_id de Cloudinary u otro path relativo, pasa por helper
  try {
    return cld(ev.image, {
      w: width,
      h: height,
      crop: 'fill',
      gravity: 'auto',
    });
  } catch {
    return EVENT_PLACEHOLDER;
  }
}

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Normaliza respuesta de /api/events → items[]
function normalizeEventsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.events)) return payload.events;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

// Normaliza respuesta de /api/fixed-events → items[]
function normalizeFixedEventsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function normalizeDate(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function formatYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function isSameMonth(d, ref) {
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth()
  );
}

function monthMatrix(baseDate) {
  // 6 filas x 7 columnas (42 celdas) comenzando domingo
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay(); // 0-dom .. 6-sáb
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  // Relleno previo
  for (let i = 0; i < startDay; i++) {
    const date = new Date(year, month, i - startDay + 1);
    cells.push({ date, inMonth: false });
  }
  // Días del mes
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), inMonth: true });
  }
  // Relleno posterior hasta 42
  while (cells.length < 42) {
    const last = cells[cells.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    cells.push({ date: next, inMonth: false });
  }
  // Agrupar por semanas
  const weeks = [];
  for (let i = 0; i < 42; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/**
 * Decide si un evento fijo debe renderizarse en una fecha concreta.
 * - Coincide el weekday
 * - Si firstSundayOnly = true → solo la primera ocurrencia de ese weekday del mes (día 1–7)
 */
function shouldRenderFixedEventOnDate(fixedEvent, date) {
  const weekday = Number(fixedEvent.weekday ?? 0);
  if (date.getDay() !== weekday) return false;

  if (fixedEvent.firstSundayOnly) {
    const dayOfMonth = date.getDate();
    return dayOfMonth <= 7;
  }
  return true;
}

// Helpers de color para eventos fijos
function getFixedEventColorScheme(ev) {
  return ev?.color || 'accent';
}

function getFixedEventBg(ev, past) {
  const scheme = getFixedEventColorScheme(ev);
  const shade = past ? '300' : '500';
  return `${scheme}.${shade}`;
}

function getFixedEventHoverBg(ev, past) {
  const scheme = getFixedEventColorScheme(ev);
  const shade = past ? '400' : '600';
  return `${scheme}.${shade}`;
}

export default function Calendario() {
  const offMonthBg = useColorModeValue('gray.100', 'whiteAlpha.50');
  // Carga remota
  const [items, setItems] = useState([]); // eventos puntuales
  const [fixedEvents, setFixedEvents] = useState([]); // eventos fijos
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const [eventsPayload, fixedPayload] = await Promise.all([
          fetchJson(`${API}/events?limit=1000`),
          fetchJson(`${API}/fixed-events`),
        ]);
        if (!alive) return;
        setItems(normalizeEventsPayload(eventsPayload));
        setFixedEvents(normalizeFixedEventsPayload(fixedPayload));
      } catch (e) {
        if (alive) setErr(e?.message || 'Error al cargar eventos');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Rango de años según eventos (+/- 1 de colchón)
  const years = useMemo(() => {
    const now = new Date().getFullYear();
    if (!items.length) return [now - 1, now, now + 1];
    let minY = Infinity;
    let maxY = -Infinity;
    for (const ev of items) {
      const y = new Date(ev.date).getFullYear();
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    const start = Math.min(minY, now) - 1;
    const end = Math.max(maxY, now) + 1;
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [items]);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // Estado de mes/año actual (siempre day=1)
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Selects controlados por currentMonth
  const selectedMonth = currentMonth.getMonth();
  const selectedYear = currentMonth.getFullYear();

  // Toggle móvil: ver todos
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [showAllMobile, setShowAllMobile] = useState(false);

  // Modal / selección
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [imgLoading, setImgLoading] = useState(true);

  const isToday = (d) => normalizeDate(d).getTime() === today.getTime();
  const isPast = (evDate) => normalizeDate(evDate) < today;

  // Mapear eventos por día (solo puntuales)
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of items) {
      if (!ev.date) continue;
      const startDate = normalizeDate(ev.date);
      const endDate = ev.end_date ? normalizeDate(ev.end_date) : new Date(startDate);

      // Aseguramos que endDate no sea menor que startDate
      if (endDate < startDate) endDate.setTime(startDate.getTime());

      // Iteramos día por día desde start hasta end
      const current = new Date(startDate);
      while (current <= endDate) {
        const key = formatYMD(current);
        if (!map[key]) map[key] = [];
        map[key].push(ev);

        // Avanzamos 1 día
        current.setDate(current.getDate() + 1);
      }
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    }
    return map;
  }, [items]);

  // Navegación mes
  const prevMonth = () => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() - 1);
    setCurrentMonth(d);
  };
  const nextMonth = () => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() + 1);
    setCurrentMonth(d);
  };

  // Cambios por selects
  const onChangeMonth = (e) => {
    const m = Number(e.target.value);
    const d = new Date(currentMonth);
    d.setMonth(m);
    setCurrentMonth(d);
  };
  const onChangeYear = (e) => {
    const y = Number(e.target.value);
    const d = new Date(currentMonth);
    d.setFullYear(y);
    setCurrentMonth(d);
  };

  const weeks = monthMatrix(currentMonth);
  const monthLabel = useMemo(
    () =>
      currentMonth.toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      }),
    [currentMonth],
  );

  const openEvent = (ev) => {
    setImgLoading(true);
    setSelectedEvent(ev);
    onOpen();
  };

  // Lista móvil: solo eventos puntuales (los recurrentes se muestran en caja aparte)
  const mobileList = useMemo(() => {
    const base = showAllMobile
      ? items
        .slice()
        .sort((a, b) => new Date(a.date) - new Date(b.date))
      : items
        .filter((ev) =>
          isSameMonth(new Date(ev.date), currentMonth),
        )
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    return base;
  }, [items, currentMonth, showAllMobile]);

  const activeFixedEvents = useMemo(
    () => fixedEvents.filter((f) => f.isActive !== false),
    [fixedEvents],
  );

  if (loading) {
    return (
      <Box bg="gray.50" flex="1">
        <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
          <HStack justify="center" py={10}>
            <Spinner size="lg" />
            <Text> Cargando eventos…</Text>
          </HStack>
        </Container>
      </Box>
    );
  }

  if (err) {
    return (
      <Box bg="gray.50" flex="1">
        <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
          <Heading size="md" color="red.600">
            Error al cargar eventos
          </Heading>
          <Text color="gray.700" mt={2}>
            {String(err)}
          </Text>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="bg.default" flex="1">
      <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
        {/* Header de controles */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={4}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          mb={6}
        >
          <Heading
            size="lg"
            color="text.default"
            textAlign={{ base: 'center', md: 'left' }}
          >
            Calendario
          </Heading>

          <Flex
            gap={3}
            align="center"
            justify={{ base: 'center', md: 'flex-end' }}
            wrap="wrap"
          >
            {/* Flechas navegación */}
            <HStack>
              <IconButton
                aria-label="Mes anterior"
                icon={<ArrowLeftIcon color="text.muted" />}
                variant="outline"
                onClick={prevMonth}
                _hover={{ bg: 'blackAlpha.200' }}
              />
              <Text
                fontWeight="semibold"
                minW="180px"
                textAlign="center"
                textTransform="capitalize"
                color="text.default"
              >
                {monthLabel}
              </Text>
              <IconButton
                aria-label="Mes siguiente"
                icon={<ArrowRightIcon color="text.muted" />}
                variant="outline"
                onClick={nextMonth}
                _hover={{ bg: 'blackAlpha.200' }}
              />
            </HStack>

            {/* Selects de Mes / Año */}
            <HStack>
              <Select
                value={selectedMonth}
                onChange={onChangeMonth}
                minW="130px"
                bg="bg.card"
                borderColor="border.default"
              >
                {MONTHS.map((m, idx) => (
                  <option key={m} value={idx}>
                    {m}
                  </option>
                ))}
              </Select>
              <Select
                value={selectedYear}
                onChange={onChangeYear}
                maxW="120px"
                bg="bg.card"
                borderColor="border.default"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </HStack>

            {/* Toggle móvil: ver todos los meses */}
            {isMobile && (
              <FormControl
                display="flex"
                alignItems="center"
                w="auto"
              >
                <FormLabel
                  htmlFor="showAll"
                  mb="0"
                  color="blue.700"
                  fontSize="sm"
                >
                  Ver todos los meses
                </FormLabel>
                <Switch
                  id="showAll"
                  isChecked={showAllMobile}
                  onChange={(e) => setShowAllMobile(e.target.checked)}
                  colorScheme="green" // color del "thumb/track" cuando está ON
                  sx={{
                    // Track (barra) apagado
                    ".chakra-switch__track": {
                      bg: "red.500",
                    },
                    // Track (barra) encendido
                    ".chakra-switch__track[data-checked]": {
                      bg: "green.500",
                    },
                    // Hover opcional (aporta contraste)
                    ".chakra-switch__track:hover": {
                      bg: "red.600",
                    },
                    ".chakra-switch__track[data-checked]:hover": {
                      bg: "green.600",
                    },
                  }}
                />
              </FormControl>
            )}
          </Flex>
        </Flex>

        {/* Caja resumen de eventos recurrentes */}
        {!isMobile && activeFixedEvents.length > 0 && (
          <Box
            mb={6}
            bg="bg.card"
            borderRadius="lg"
            border="1px solid"
            borderColor="border.default"
            p={4}
            boxShadow="sm"
          >
            <Heading size="sm" color="text.default" mb={2}>
              Eventos recurrentes (horario fijo)
            </Heading>
            <VStack align="start" spacing={2}>
              {activeFixedEvents.map((ev) => {
                const colorScheme = getFixedEventColorScheme(ev);
                const weekdayLabel =
                  WEEKDAY_LABELS_LONG[Number(ev.weekday ?? 0)];
                return (
                  <HStack
                    key={ev.id}
                    spacing={3}
                    align="center"
                    flexWrap="wrap"
                  >
                    <Badge colorScheme={colorScheme}>
                      {weekdayLabel}
                    </Badge>
                    {ev.time && (
                      <HStack
                        spacing={1}
                        fontSize="sm"
                        color="text.muted"
                      >
                        <FiClock />
                        <Text>{ev.time}</Text>
                      </HStack>
                    )}
                    <Text
                      fontSize="sm"
                      color="text.default"
                      fontWeight="semibold"
                    >
                      {ev.title}
                    </Text>
                    {ev.firstSundayOnly && (
                      <Text
                        fontSize="xs"
                        color="purple.600"
                      >
                        Solo el primer{' '}
                        {weekdayLabel.toLowerCase()} de cada
                        mes.
                      </Text>
                    )}
                  </HStack>
                );
              })}
            </VStack>
          </Box>
        )}

        {/* Vista móvil (lista) / desktop (calendario) */}
        {isMobile ? (
          mobileList.length ? (
            <VStack align="stretch" spacing={4}>
              {mobileList.map((ev, idx) => {
                const evDate = new Date(ev.date);
                const past = isPast(evDate);
                const isTodayFlag = isToday(evDate);
                const imgSrc = getEventImageSrc(ev);

                return (
                  <Box
                    key={`${ev.title}-${ev.date}-${idx}`}
                    bg="bg.card"
                    border="1px solid"
                    borderColor="border.default"
                    borderRadius="xl"
                    overflow="hidden"
                    boxShadow="sm"
                  >
                    <Image
                      src={imgSrc}
                      alt={ev.title}
                      w="100%"
                      h="180px"
                      objectFit="cover"
                      filter={
                        past ? 'grayscale(100%)' : 'none'
                      }
                      fallbackSrc={EVENT_PLACEHOLDER}
                    />
                    <VStack
                      align="start"
                      spacing={2}
                      p={4}
                    >
                      <Heading
                        size="md"
                        color="text.default"
                        noOfLines={1}
                      >
                        {ev.title}
                      </Heading>
                      <Text
                        color="text.muted"
                        fontWeight="medium"
                      >
                        {isTodayFlag && (
                          <Badge
                            colorScheme="blue"
                            mr={2}
                          >
                            ¡Hoy!
                          </Badge>
                        )}
                        {new Date(
                          ev.date,
                        ).toLocaleDateString()}
                        {ev.end_date &&
                          normalizeDate(ev.end_date).getTime() !== normalizeDate(ev.date).getTime() &&
                          ` - ${new Date(ev.end_date).toLocaleDateString()}`}
                      </Text>
                      <HStack
                        spacing={3}
                        color="text.muted"
                        fontSize="sm"
                      >
                        {ev.time && (
                          <HStack spacing={1}>
                            <FiClock />
                            <Text>{ev.time}</Text>
                          </HStack>
                        )}
                        {ev.place && (
                          <HStack spacing={1}>
                            <FiMapPin />
                            <Text noOfLines={1}>
                              {ev.place}
                            </Text>
                          </HStack>
                        )}
                      </HStack>
                      {ev.description && (
                        <Text
                          color="text.muted"
                          noOfLines={3}
                        >
                          {ev.description}
                        </Text>
                      )}
                      <Button
                        mt={2}
                        size="sm"
                        variant="outline"
                        colorScheme="gray"
                        onClick={() => openEvent(ev)}
                      >
                        Ver detalle
                      </Button>
                    </VStack>
                  </Box>
                );
              })}
            </VStack>
          ) : (
            <Box
              bg="bg.card"
              border="1px solid"
              borderColor="border.default"
              borderRadius="md"
              p={6}
              textAlign="center"
            >
              <Text color="text.muted">
                No hay eventos para este periodo.
              </Text>
            </Box>
          )
        ) : (
          <>
            <SimpleGrid
              columns={7}
              spacing={2}
              mb={2}
            >
              {WEEKDAYS.map((w) => (
                <Box
                  key={w}
                  py={2}
                  textAlign="center"
                  fontWeight="semibold"
                  color="text.muted"
                >
                  {w}
                </Box>
              ))}
            </SimpleGrid>

            <VStack spacing={2} align="stretch">
              {weeks.map((week, wi) => (
                <SimpleGrid
                  key={wi}
                  columns={7}
                  spacing={2}
                >
                  {week.map(
                    ({ date, inMonth }, di) => {
                      const key = formatYMD(date);
                      const dayEvents = (eventsByDay[key] || [])
                        .slice()
                        .sort((a, b) =>
                          (a.time || '').localeCompare(
                            b.time || '',
                          ),
                        );
                      const todayFlag = isToday(date);

                      const recurringForDay =
                        activeFixedEvents.filter((f) =>
                          shouldRenderFixedEventOnDate(
                            f,
                            date,
                          ),
                        );

                      // Normalizamos a entradas para renderizar (puntuales + recurrentes)
                      const entries = [
                        ...dayEvents.map((ev) => ({
                          kind: 'single',
                          id: `single-${ev.id ?? ev.title
                            }-${di}`,
                          title: ev.title,
                          time: ev.time,
                          place: ev.place,
                          raw: ev,
                        })),
                        ...recurringForDay.map((ev) => ({
                          kind: 'recurring',
                          id: `recurring-${ev.id}-${di}`,
                          title: ev.title,
                          time: ev.time,
                          place: ev.place,
                          raw: ev,
                        })),
                      ];

                      const visibleEntries =
                        entries.slice(0, 3);

                      return (
                        <Box
                          key={`${key}-${di}`}
                          bg={
                            inMonth ? 'bg.card' : offMonthBg
                          }
                          border="1px solid"
                          borderColor={
                            todayFlag
                              ? 'accent.600'
                              : 'border.default'
                          }
                          borderRadius="md"
                          minH="110px"
                          p={2}
                        >
                          <Flex
                            justify="space-between"
                            align="center"
                            mb={2}
                          >
                            <Text
                              fontSize="sm"
                              fontWeight={
                                todayFlag
                                  ? 'extrabold'
                                  : 'semibold'
                              }
                              color={
                                inMonth
                                  ? 'text.default'
                                  : 'text.muted'
                              }
                            >
                              {date.getDate()}
                            </Text>
                            {todayFlag && (
                              <Badge colorScheme="blue">
                                Hoy
                              </Badge>
                            )}
                          </Flex>

                          <VStack
                            align="stretch"
                            spacing={1}
                          >
                            {visibleEntries.map(
                              (entry) => {
                                const pastFlag =
                                  isPast(date);
                                const isRecurring =
                                  entry.kind ===
                                  'recurring';

                                const bg = isRecurring
                                  ? getFixedEventBg(
                                    entry.raw,
                                    pastFlag,
                                  )
                                  : pastFlag
                                    ? 'gray.400'
                                    : 'brand.600';

                                const hoverBg =
                                  isRecurring
                                    ? getFixedEventHoverBg(
                                      entry.raw,
                                      pastFlag,
                                    )
                                    : pastFlag
                                      ? 'gray.500'
                                      : 'brand.700';

                                const label = `${entry.time
                                  ? `${entry.time} · `
                                  : ''
                                  }${entry.title}`;

                                const handleClick =
                                  () => {
                                    if (
                                      entry.kind ===
                                      'single'
                                    ) {
                                      openEvent(
                                        entry.raw,
                                      );
                                    } else {
                                      // Para recurrentes, inyectamos la fecha concreta de esa celda
                                      openEvent({
                                        ...entry.raw,
                                        date: date.toISOString(),
                                      });
                                    }
                                  };

                                return (
                                  <Button
                                    key={entry.id}
                                    size="xs"
                                    variant="solid"
                                    bg={bg}
                                    _hover={{
                                      bg: hoverBg,
                                    }}
                                    color="white"
                                    justifyContent="flex-start"
                                    onClick={
                                      handleClick
                                    }
                                    maxW="100%"
                                    w="100%"
                                    overflow="hidden"
                                    whiteSpace="nowrap"
                                    textOverflow="ellipsis"
                                    title={
                                      entry.kind ===
                                        'recurring'
                                        ? `Recurrente · ${WEEKDAY_LABELS_LONG[
                                        date.getDay()
                                        ]
                                        }`
                                        : undefined
                                    }
                                  >
                                    {entry.kind ===
                                      'recurring'
                                      ? '⟳ '
                                      : ''}
                                    {label}
                                  </Button>
                                );
                              },
                            )}
                            {entries.length > 3 && (
                              <Text
                                fontSize="xs"
                                color="text.muted"
                              >
                                +
                                {entries.length -
                                  3}{' '}
                                más…
                              </Text>
                            )}
                          </VStack>
                        </Box>
                      );
                    },
                  )}
                </SimpleGrid>
              ))}
            </VStack>
          </>
        )}
      </Container>

      {/* Modal de detalle */}
      {selectedEvent && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          size={{ base: 'full', md: 'lg' }}
          isCentered
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent bg="bg.card">
            <ModalHeader color="text.default">
              {selectedEvent.title}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box position="relative" w="100%" h={{ base: '200px', md: '260px' }} mb={4} borderRadius="md" overflow="hidden">
                {imgLoading && (
                  <Flex
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    align="center"
                    justify="center"
                    bg="gray.100"
                    zIndex={1}
                  >
                    <Spinner size="lg" color="blue.500" />
                  </Flex>
                )}
                <Image
                  src={getEventImageSrc(selectedEvent, {
                    width: 1000,
                    height: 600,
                  })}
                  alt={selectedEvent.title}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  fallbackSrc={EVENT_PLACEHOLDER}
                  onLoad={() => setImgLoading(false)}
                />
              </Box>
              <VStack align="start" spacing={3}>
                <Text
                  color="text.muted"
                  fontWeight="medium"
                >
                  {normalizeDate(
                    selectedEvent.date,
                  ).getTime() ===
                    today.getTime() && (
                      <Badge colorScheme="blue" mr={2}>
                        ¡Hoy!
                      </Badge>
                    )}
                  {selectedEvent.date
                    ? <>
                      {new Date(selectedEvent.date).toLocaleDateString()}
                      {selectedEvent.end_date &&
                        normalizeDate(selectedEvent.end_date).getTime() !== normalizeDate(selectedEvent.date).getTime() &&
                        ` - ${new Date(selectedEvent.end_date).toLocaleDateString()}`}
                    </>
                    : ''}
                </Text>
                <HStack
                  spacing={4}
                  color="text.muted"
                >
                  {selectedEvent.time && (
                    <HStack spacing={1}>
                      <FiClock />
                      <Text>
                        {selectedEvent.time}
                      </Text>
                    </HStack>
                  )}
                  {selectedEvent.place && (
                    <HStack spacing={1}>
                      <FiMapPin />
                      <Text>
                        {selectedEvent.place}
                      </Text>
                    </HStack>
                  )}
                </HStack>
                {selectedEvent.description && (
                  <Text color="text.muted">
                    {selectedEvent.description}
                  </Text>
                )}
                {selectedEvent.firstSundayOnly &&
                  typeof selectedEvent.weekday !==
                  'undefined' && (
                    <Text
                      fontSize="sm"
                      color="purple.600"
                    >
                      Solo el primer{' '}
                      {WEEKDAY_LABELS_LONG[
                        Number(
                          selectedEvent.weekday ??
                          0,
                        )
                      ]?.toLowerCase()}{' '}
                      de cada mes.
                    </Text>
                  )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                onClick={onClose}
                colorScheme="gray"
              >
                Cerrar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}
